/**
 * Tomorrow's outfits: your members' combinations for tomorrow, generated
 * overnight and reviewed before the evening deadline.
 *
 * The assigned stylist reviews their own members first and anyone can help
 * with the rest. Once the API reports ownership (`is_mine` and the assigned
 * stylist on each row) the page opens on "Mine" with a Mine / Others' / All
 * control. An API that does not report it gets the queue exactly as before:
 * one list, no control, and no `scope` parameter on any request.
 *
 * Each member has three looks a day, listed together in look order. Once the
 * API numbers its looks, the page also reads the day's replacements still
 * generating and the looks that could not be made, and lists them beside the
 * member's other looks with nothing to act on.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw01 } from '@untitledui/icons';
import { useSearchParams } from 'react-router';
import { Button } from '@ui/components/base/buttons/button';
import { TextField } from '@ui/console/forms';
import { MetaItem, StatRow, StatTile } from '@ui/console/primitives';
import { Page, ResourceState } from './components';
import { CutoffCountdown } from './CutoffCountdown';
import { GarmentThumbs, MemberAvatar } from './member-ui';
import { formatCountdown, millisecondsToCutoff, weekdayFor } from './review-cutoff';
import {
  byMemberThenLook,
  reportsLooks,
  reportsOwnership,
  reviewService,
  type QueueScope,
  type QueuedOutfit,
  type ReviewQueue,
  type ReviewQueueRequest,
  type ReviewService,
} from './review-service';

export function combinationSummary(outfit: QueuedOutfit): string {
  return outfit.garments.map((garment) => garment.title).join(' + ') || 'No garments';
}

export function useReviewQueue(service: ReviewService, request: ReviewQueueRequest = {}) {
  const reviewStatus = request.reviewStatus ?? 'awaiting_review';
  const limit = request.limit ?? 50;
  const offset = request.offset ?? 0;
  const q = request.q ?? '';
  const scope = request.scope;
  const [resource, setResource] = useState<{
    loading: boolean;
    data?: ReviewQueue;
    error?: string;
  }>({ loading: true });
  const load = useCallback(async () => {
    setResource((current) => ({ ...current, loading: true, error: undefined }));
    try {
      setResource({
        loading: false,
        data: await service.loadQueue({
          reviewStatus,
          limit,
          offset,
          ...(q ? { q } : {}),
          ...(scope ? { scope } : {}),
        }),
      });
    } catch (cause) {
      setResource({
        loading: false,
        error: cause instanceof Error ? cause.message : "Tomorrow's outfits could not be loaded.",
      });
    }
  }, [service, reviewStatus, limit, offset, q, scope]);
  useEffect(() => void load(), [load]);
  return { resource, load };
}

export function parseScope(value: string | null): QueueScope | undefined {
  return value === 'mine' || value === 'others' || value === 'all' ? value : undefined;
}

type ScopeCounts = Partial<Record<QueueScope, number>>;

type ScopedQueue = {
  queue: ReviewQueue;
  /** The API reports ownership, so the page shows the scope control. */
  scoped: boolean;
  counts: ScopeCounts;
  /** The day's looks no one can act on yet: replacements generating, looks not made. */
  unactionable: QueuedOutfit[];
};

function countsFrom(scope: QueueScope, total: number, other?: ReviewQueue): ScopeCounts {
  // `other` is the complementary read: Mine when viewing All, All otherwise.
  if (scope === 'all') {
    return other ? { all: total, mine: other.total, others: total - other.total } : { all: total };
  }
  if (!other) return { [scope]: total };
  const all = other.total;
  return scope === 'mine'
    ? { all, mine: total, others: all - total }
    : { all, others: total, mine: all - total };
}

/**
 * Read one page of the queue in `scope`. Until this page has seen the API
 * report ownership it reads without a scope - the request every API version
 * understands - and only asks for the scope once the answer shows it can.
 * The counts come from one extra one-row read and are dropped if it fails.
 */
async function readScopedQueue(
  service: ReviewService,
  request: { scope: QueueScope; offset: number; q: string },
  ownershipKnown: boolean,
): Promise<ScopedQueue> {
  const base = { reviewStatus: 'awaiting_review' as const, ...(request.q ? { q: request.q } : {}) };
  const count = (scope: QueueScope) =>
    service.loadQueue({ ...base, scope, limit: 1, offset: 0 }).catch(() => undefined);
  if (!ownershipKnown) {
    const all = await service.loadQueue({ ...base, offset: request.offset });
    if (!reportsOwnership(all.outfits)) {
      return { queue: all, scoped: false, counts: {}, unactionable: [] };
    }
    if (request.scope === 'all') {
      return {
        queue: all,
        scoped: true,
        counts: countsFrom('all', all.total, await count('mine')),
        unactionable: [],
      };
    }
    const queue = await service.loadQueue({
      ...base,
      scope: request.scope,
      offset: request.offset,
    });
    return {
      queue,
      scoped: true,
      counts: countsFrom(request.scope, queue.total, all),
      unactionable: [],
    };
  }
  const [queue, other] = await Promise.all([
    service.loadQueue({ ...base, scope: request.scope, offset: request.offset }),
    count(request.scope === 'all' ? 'mine' : 'all'),
  ]);
  return {
    queue,
    scoped: true,
    counts: countsFrom(request.scope, queue.total, other),
    unactionable: [],
  };
}

const UNACTIONABLE_LIMIT = 200;

/**
 * The day's replacements still generating and looks that could not be made,
 * in the same scope and search as the queue. Best effort: a failed read lists
 * nothing extra. Only rows for this page's members are kept, plus - on the
 * first page - members with nothing awaiting review on it.
 */
async function readUnactionable(
  service: ReviewService,
  queue: ReviewQueue,
  request: { scope?: QueueScope | undefined; q: string },
): Promise<QueuedOutfit[]> {
  const read = (reviewStatus: 'awaiting_generation' | 'unfilled') =>
    service
      .loadQueue({
        reviewStatus,
        limit: UNACTIONABLE_LIMIT,
        offset: 0,
        ...(request.q ? { q: request.q } : {}),
        ...(request.scope ? { scope: request.scope } : {}),
      })
      .then((page) => page.outfits)
      .catch(() => [] as QueuedOutfit[]);
  const [generating, unfilled] = await Promise.all([read('awaiting_generation'), read('unfilled')]);
  const onPage = new Set(queue.outfits.map((outfit) => outfit.memberId));
  return [...generating.filter((outfit) => outfit.replacesSlotId), ...unfilled].filter(
    (outfit) =>
      outfit.forDate === queue.targetDate && (onPage.has(outfit.memberId) || queue.offset === 0),
  );
}

function useScopedQueue(
  service: ReviewService,
  request: { scope: QueueScope; offset: number; q: string },
) {
  const { scope, offset, q } = request;
  const ownershipKnown = useRef(false);
  const looksKnown = useRef(false);
  const [resource, setResource] = useState<{
    loading: boolean;
    data?: ScopedQueue;
    error?: string;
  }>({ loading: true });
  const load = useCallback(async () => {
    setResource((current) => ({ ...current, loading: true, error: undefined }));
    try {
      const data = await readScopedQueue(service, { scope, offset, q }, ownershipKnown.current);
      if (data.scoped) ownershipKnown.current = true;
      if (reportsLooks(data.queue.outfits)) looksKnown.current = true;
      if (looksKnown.current) {
        data.unactionable = await readUnactionable(service, data.queue, {
          q,
          ...(data.scoped ? { scope } : {}),
        });
      }
      setResource({ loading: false, data });
    } catch (cause) {
      setResource({
        loading: false,
        error: cause instanceof Error ? cause.message : "Tomorrow's outfits could not be loaded.",
      });
    }
  }, [service, scope, offset, q]);
  useEffect(() => void load(), [load]);
  return { resource, load };
}

/** Where a row's Review link goes, carrying the page and search so the review can find it. */
export function reviewHref(slotId: string, offset: number, q: string, scope?: QueueScope): string {
  const params = new URLSearchParams({ offset: String(offset) });
  if (q) params.set('q', q);
  if (scope) params.set('scope', scope);
  return `/review/${encodeURIComponent(slotId)}?${params.toString()}`;
}

/** Whose member this is, in the words a stylist uses. */
export function assignedLabel(outfit: QueuedOutfit): string | undefined {
  const owner = outfit.ownership;
  if (!owner) return undefined;
  if (owner.isMine) return 'Assigned to you';
  if (!owner.assignedStylistId) return 'Unassigned';
  return `Assigned to ${owner.assignedStylistName ?? 'another stylist'}`;
}

/** What a row without a Review link says instead, or undefined for a reviewable look. */
export function unactionableLabel(outfit: QueuedOutfit): string | undefined {
  if (outfit.reviewStatus === 'unfilled') return "Couldn't create this look";
  if (outfit.reviewStatus === 'awaiting_generation') return 'Generating replacement…';
  return undefined;
}

function QueueRow({ outfit, href, now }: { outfit: QueuedOutfit; href: string; now: Date }) {
  const name = outfit.context.displayName;
  const assigned = assignedLabel(outfit);
  const unactionable = unactionableLabel(outfit);
  return (
    <li className="flex flex-col gap-3 rounded-xl border border-secondary bg-primary p-4 sm:flex-row sm:items-center">
      <div className="flex min-w-0 items-center gap-3 sm:w-56 sm:shrink-0">
        <MemberAvatar name={name} src={outfit.context.avatarUrl} />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-semibold text-primary">{name}</span>
          {/* Members share first names; the masked email tells two of them apart. */}
          {outfit.context.email ? (
            <span className="truncate text-xs text-tertiary">{outfit.context.email}</span>
          ) : null}
          <span className="text-xs text-tertiary">
            {outfit.lookIndex ? `Look ${outfit.lookIndex} · ` : ''}
            {outfit.garments.length} garment{outfit.garments.length === 1 ? '' : 's'}
            {outfit.location ? ` · ${outfit.location}` : ''}
          </span>
          {assigned ? <span className="truncate text-xs text-tertiary">{assigned}</span> : null}
        </div>
      </div>
      <div className="min-w-0 flex-1" title={combinationSummary(outfit)}>
        <GarmentThumbs garments={outfit.garments} label={`${name} garment images`} max={8} />
      </div>
      {unactionable ? (
        <span className="text-sm text-tertiary">{unactionable}</span>
      ) : (
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <span className="text-xs text-tertiary">
            {formatCountdown(millisecondsToCutoff(now, outfit.forDate))} left
          </span>
          <Button size="sm" href={href}>
            Review
          </Button>
        </div>
      )}
    </li>
  );
}

function QueueList({
  outfits,
  label,
  offset,
  q,
  scope,
  now,
}: {
  outfits: QueuedOutfit[];
  label: string;
  offset: number;
  q: string;
  scope?: QueueScope | undefined;
  now: Date;
}) {
  return (
    <ul aria-label={label} className="flex flex-col gap-2">
      {outfits.map((outfit) => (
        <QueueRow
          key={outfit.slotId}
          outfit={outfit}
          href={reviewHref(outfit.slotId, offset, q, scope)}
          now={now}
        />
      ))}
    </ul>
  );
}

const SCOPE_EMPTY: Record<QueueScope, { title: string; message: string }> = {
  mine: {
    title: 'Nothing left for your members',
    message: "Your members' outfits for tomorrow are all reviewed.",
  },
  others: {
    title: "Nothing waiting on others' members",
    message: "No other stylist's member has an outfit awaiting review.",
  },
  all: {
    title: 'Nothing queued for review',
    message: 'No outfits have been generated for tomorrow yet. They arrive overnight.',
  },
};

const SCOPE_HINT: Record<QueueScope, string> = {
  mine: 'Members assigned to you',
  others: "Other stylists' members",
  all: 'Every pending outfit, yours first',
};

const REPLACEMENT_POLL_MS = 30_000;

export function ReviewQueueScreen({
  service = reviewService,
  now,
}: {
  service?: ReviewService;
  now?: Date;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialOffset = Number(searchParams.get('offset'));
  const [offset, setOffset] = useState(
    Number.isInteger(initialOffset) && initialOffset >= 0 ? initialOffset : 0,
  );
  // Stylists review only their own members; covering for other stylists is not offered.
  const scope: QueueScope = 'mine';
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [q, setQ] = useState(search.trim());
  // Debounce the server-side name search.
  useEffect(() => {
    const next = search.trim();
    if (next === q) return undefined;
    const timer = setTimeout(() => {
      setQ(next);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [q, search]);

  const { resource, load } = useScopedQueue(service, { scope, offset, q });
  const scoped = resource.data?.scoped ?? false;
  useEffect(() => {
    const next = new URLSearchParams();
    if (offset) next.set('offset', String(offset));
    if (q) next.set('q', q);
    if (scoped && scope !== 'mine') next.set('scope', scope);
    setSearchParams(next, { replace: true });
  }, [offset, q, scope, scoped, setSearchParams]);

  const queue = resource.data?.queue;
  const unactionable = resource.data?.unactionable ?? [];
  const generating = unactionable.some((outfit) => outfit.reviewStatus === 'awaiting_generation');
  // A replacement joins the review queue once it is generated; look again until it has.
  useEffect(() => {
    if (!generating) return undefined;
    const timer = setTimeout(() => void load(), REPLACEMENT_POLL_MS);
    return () => clearTimeout(timer);
  }, [generating, load, resource.data]);
  const clock = now ?? new Date();
  const total = queue?.total ?? 0;
  const loaded = queue?.outfits.length ?? 0;
  const empty = scoped ? SCOPE_EMPTY[scope] : SCOPE_EMPTY.all;

  return (
    <Page
      title="Tomorrow's outfits"
      description="Your members' combinations for tomorrow, generated overnight. Approve it as it is, or change it and say why."
      actions={
        <Button
          size="sm"
          color="secondary"
          iconLeading={RefreshCw01}
          isLoading={resource.loading}
          onClick={() => void load()}
        >
          Refresh
        </Button>
      }
      meta={
        queue ? (
          <>
            <MetaItem
              label="Ships for"
              value={`${weekdayFor(queue.targetDate)} · ${queue.targetDate}`}
            />
            <MetaItem label="Reviewing" value="Garment combinations, before the try-on picture" />
          </>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full max-w-sm">
          <TextField
            label="Find a member"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>
      </div>
      <ResourceState
        loading={resource.loading && !queue}
        loadingLabel="tomorrow's outfits"
        error={resource.error}
        empty={total === 0 && unactionable.length === 0}
        emptyTitle={q ? 'No outfits for that member' : empty.title}
        emptyMessage={q ? 'No outfit awaiting review matches that name.' : empty.message}
        onRetry={() => void load()}
      >
        {queue ? (
          <div className="flex flex-col gap-6">
            <StatRow>
              <StatTile
                label="Awaiting review"
                value={String(total)}
                hint={
                  q
                    ? 'Matching this search'
                    : scoped
                      ? SCOPE_HINT[scope]
                      : 'All pending outfits in the queue'
                }
              />
              <StatTile
                label="Loaded on this page"
                value={String(loaded)}
                hint={loaded ? `Outfits ${queue.offset + 1}–${queue.offset + loaded}` : undefined}
              />
              <CutoffCountdown targetDate={queue.targetDate} {...(now ? { now } : {})} />
              <StatTile
                label="Ships for"
                value={weekdayFor(queue.targetDate)}
                hint={queue.targetDate}
              />
            </StatRow>
            <QueueList
              outfits={byMemberThenLook([...queue.outfits, ...unactionable])}
              label="Queued outfits"
              offset={queue.offset}
              q={q}
              scope={scoped ? scope : undefined}
              now={clock}
            />
            {queue.total > queue.limit ? (
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm text-tertiary">
                  {queue.offset + 1}–{Math.min(queue.offset + loaded, queue.total)} of {queue.total}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    color="secondary"
                    isDisabled={queue.offset === 0 || resource.loading}
                    onClick={() => setOffset(Math.max(0, queue.offset - queue.limit))}
                  >
                    Previous page
                  </Button>
                  <Button
                    size="sm"
                    color="secondary"
                    isDisabled={queue.offset + loaded >= queue.total || resource.loading}
                    onClick={() => setOffset(queue.offset + queue.limit)}
                  >
                    Next page
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </ResourceState>
    </Page>
  );
}
